import {
  BrainCircuit,
  Settings,
  Database,
  Wand2,
  Users,
  Paintbrush,
  FileText,
  Library,
  BookMarked,
  Activity,
  Compass,
  LayoutGrid,
  GitBranch,
  FileSearch,
  History,
  Workflow,
  BookOpen,
  GitCompare,
  ShieldCheck,
  Network,
  Sprout,
  LayoutTemplate,
  Package,
  Files,
  Tags,
  Gauge,
  AlertTriangle,
  Route as RouteIcon,
  Layers,
  Cpu,
  SlidersHorizontal,
  PlugZap,
} from 'lucide-react';

export interface NavItem {
  label: string;
  to: string;
  icon: React.ElementType;
  section: string;
  badge?: string;
  /** When set, the Sidebar resolves the badge count from this dynamic key. */
  badgeKey?: 'pending_review';
}

/**
 * Sections that are always rendered flat at the top of the nav.
 * Everything else is bucketed into a collapsible group.
 */
export const pinnedSections = new Set([
  'Creative Strategy',
  'Creative Memory',
  'Creative Skills',
]);

export interface NavBucket {
  label: string;
  icon: React.ElementType;
  /** Section names contained in this bucket, in display order. */
  sections: string[];
}

/**
 * Top-level collapsible buckets shown below the pinned sections.
 * The order here controls render order in the sidebar.
 */
export const navBuckets: NavBucket[] = [
  {
    label: 'Creative',
    icon: Layers,
    sections: ['Creative Review', 'Creative Production'],
  },
  {
    label: 'Intelligence',
    icon: Cpu,
    sections: ['Decision Ontology', 'Consumer Preference Graph', 'Knowledge Acquisition'],
  },
  {
    label: 'System',
    icon: SlidersHorizontal,
    sections: ['Infrastructure'],
  },
];

export const navigation: NavItem[] = [
  // ── Pinned sections ──────────────────────────────────────────────────────

  // Creative Strategy — Library (category level), Dockets (one per client), their evidence and templates
  { label: 'Library',   to: '/knowledge/creative-strategy/library', icon: Layers,  section: 'Creative Strategy' },
  { label: 'Dockets',   to: '/knowledge/creative-strategy/dockets',   icon: Package, section: 'Creative Strategy' },
  { label: 'Corpus',    to: '/knowledge/creative-strategy/corpus',    icon: Files,   section: 'Creative Strategy' },
  { label: 'Templates', to: '/knowledge/creative-strategy/templates', icon: LayoutTemplate, section: 'Creative Strategy' },

  // Creative Memory — reasoning traces, playbooks, routing decisions
  { label: 'Overview',        to: '/creative-memory',             icon: Gauge,         section: 'Creative Memory' },
  { label: 'Playbooks',       to: '/creative-memory/playbooks',   icon: BookOpen,      section: 'Creative Memory' },
  { label: 'Persona Routing', to: '/creative-memory/routing',     icon: RouteIcon,     section: 'Creative Memory' },
  { label: 'Decisions',       to: '/creative-memory/decisions',   icon: History,       section: 'Creative Memory' },
  { label: 'Escalations',     to: '/creative-memory/escalations', icon: AlertTriangle, section: 'Creative Memory' },
  { label: 'Ad Taxonomy',     to: '/knowledge/catalogs/ad-taxonomy', icon: Tags,        section: 'Creative Memory' },

  // Creative Skills — catalog, library, bindings, activity
  { label: 'Skills Catalog',   to: '/skills-catalog',   icon: LayoutGrid, section: 'Creative Skills' },
  { label: 'Skill Library',    to: '/skill-library',    icon: Library,    section: 'Creative Skills' },
  { label: 'Skill Bindings',   to: '/client-playbooks', icon: BookMarked, section: 'Creative Skills' },
  { label: 'Usage & Activity', to: '/usage-activity',   icon: Activity,   section: 'Creative Skills' },

  // ── Bucketed sections ────────────────────────────────────────────────────

  // Creative Review — review / administer / tune the Creative Review agent
  { label: 'Runs',   to: '/admin/creative-review/:clientUid/runs',   icon: Activity, section: 'Creative Review' },
  { label: 'Briefs', to: '/admin/creative-review/:clientUid/briefs', icon: FileText, section: 'Creative Review' },
  { label: 'Tuning', to: '/admin/creative-review/:clientUid/tuning', icon: Wand2,    section: 'Creative Review' },

  // Creative Production — generation, brand design, frameworks, templates
  { label: 'Generation',         to: '/generation',         icon: Wand2,          section: 'Creative Production' },
  { label: 'Brand Design',       to: '/brand-design',       icon: Paintbrush,     section: 'Creative Production' },
  { label: 'Frameworks',         to: '/frameworks',         icon: Library,        section: 'Creative Production' },
  { label: 'Creative Templates', to: '/creative-templates', icon: LayoutTemplate, section: 'Creative Production' },
  { label: 'Brief Templates',    to: '/brief-templates',    icon: FileText,       section: 'Creative Production' },

  // Decision Ontology Admin UI — Phase 5
  { label: 'Versions',      to: '/admin/ontology/:clientUid/versions', icon: GitBranch, section: 'Decision Ontology' },
  { label: 'Concept Graph', to: '/admin/ontology/:clientUid/concepts', icon: Network,   section: 'Decision Ontology' },
  { label: 'Builds',        to: '/admin/ontology/:clientUid/builds',   icon: Workflow,  section: 'Decision Ontology' },
  { label: 'Explorer',      to: '/admin/ontology/:clientUid/explorer', icon: Network,   section: 'Decision Ontology' },
  { label: 'Garden',        to: '/admin/garden/:clientUid',            icon: Sprout,    section: 'Decision Ontology', badgeKey: 'pending_review' },

  // Consumer Preference Graph — cognitive graph, personas, scenarios
  { label: 'Cognitive Graph', to: '/graph',     icon: BrainCircuit, section: 'Consumer Preference Graph' },
  { label: 'Personas',        to: '/personas',  icon: Users,        section: 'Consumer Preference Graph' },
  { label: 'Scenarios',       to: '/scenarios', icon: Compass,      section: 'Consumer Preference Graph' },

  // Knowledge Acquisition — Phase 0 onward
  { label: 'Entities',      to: '/knowledge/estate/entities', icon: Database,    section: 'Knowledge Acquisition' },
  { label: 'Raw Concepts',  to: '/knowledge/estate/concepts', icon: FileSearch,  section: 'Knowledge Acquisition' },
  { label: 'Research Runs', to: '/knowledge/estate/runs',     icon: History,     section: 'Knowledge Acquisition' },
  { label: 'Lineage',       to: '/knowledge/estate/lineage',  icon: Workflow,    section: 'Knowledge Acquisition' },
  { label: 'Catalogs',      to: '/knowledge/catalogs',        icon: BookOpen,    section: 'Knowledge Acquisition' },
  { label: 'Fleet',         to: '/knowledge/fleet',           icon: Activity,    section: 'Knowledge Acquisition' },
  { label: 'Resolution',    to: '/knowledge/resolution',      icon: GitCompare,  section: 'Knowledge Acquisition' },
  { label: 'Quality',       to: '/knowledge/quality',         icon: ShieldCheck, section: 'Knowledge Acquisition' },

  // Infrastructure
  { label: 'MCP Connectors', to: '/mcp-connectors', icon: PlugZap,  section: 'Infrastructure' },
  { label: 'Settings',       to: '/settings',       icon: Settings, section: 'Infrastructure' },
];

/** Group navigation items by section, preserving order. */
export function groupBySection(items: NavItem[]) {
  const sections: { title: string; items: NavItem[] }[] = [];
  const seen = new Set<string>();

  for (const item of items) {
    if (!seen.has(item.section)) {
      seen.add(item.section);
      sections.push({ title: item.section, items: [] });
    }
    sections.find((s) => s.title === item.section)!.items.push(item);
  }
  return sections;
}

/** Replace `:clientUid` in a nav link with a concrete value, or strip the
 *  template segment when no client is selected yet. */
export function resolveNavTo(to: string, clientUid: string | null): string {
  if (!to.includes(':clientUid')) return to;
  if (!clientUid) {
    return to.replace('/:clientUid', '');
  }
  return to.replace(':clientUid', encodeURIComponent(clientUid));
}

/**
 * Convert a route template like `/admin/ontology/:clientUid/versions` into a
 * RegExp that matches concrete paths (`:param` segments become `[^/]+`).
 */
function navPatternToRegex(to: string): RegExp {
  const escaped = to
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/:\w+/g, '[^/]+');
  return new RegExp(`^${escaped}(/|$)`);
}

/**
 * Find the nav item that best matches a given pathname.
 * Picks the match whose template is longest (most specific wins).
 */
export function matchNavItem(pathname: string): NavItem | undefined {
  let best: NavItem | undefined;
  let bestLen = -1;

  for (const item of navigation) {
    const { to } = item;
    if (to === '/') {
      if (pathname === '/') { best = item; bestLen = 1; }
      continue;
    }
    if (navPatternToRegex(to).test(pathname)) {
      if (to.length > bestLen) {
        best = item;
        bestLen = to.length;
      }
    }
  }
  return best;
}
